// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useEffect, useRef, useState } from 'react';
import { CameraCalibration, UseCase } from '@tls/sw91-communication/types/com.base';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import useProjectStrategy from 'hooks/projectSettingsHooks/useProjectStrategy';
import useProjectLabelManagement from 'hooks/projectSettingsHooks/useProjectLabelManagement';
import { useProjectTagManagement } from 'hooks/projectSettingsHooks/useProjectTagManagement';
import { LabelInfo, ProjectSettingsProps } from '../../projectSelection/common/ProjectSettingTypes';
import { colorOptions, getBackgroundColorOption, getColorOption } from 'components/common/ColorOptions';
import { StrategySelector } from './subcomponents/StrategySelector';
import { ProjectNameInput } from './subcomponents/ProjectNameInput';
import { TagInput } from './subcomponents/TagInput';
import { UseCaseAdditions } from './subcomponents/useCaseAdditions';
import LabelInput from './subcomponents/LabelInput';
import useAppPermissions from 'hooks/useAppPermissions';
import useCameraCalibration from 'hooks/useCameraCalibration';
import { Headline } from '@tls/treact-ui';
import { FlagDisconnected } from '@tls/treact-ui/flags';

interface ProjectSettingsFormProps extends ProjectSettingsProps {
  useCase: UseCase;
}
const DisconnectedFlag = FlagDisconnected;

const backgroundLabel: LabelInfo = {
  labelName: 'Background',
  labelColor: getBackgroundColorOption(),
  labelCategory: '',
};

export default function ProjectSettingsForm({ onChange, containerRef, useCase }: Readonly<ProjectSettingsFormProps>) {
  const { id, duplicateSourceId } = useAppSelector(s => s.projectSettings);
  const { t } = useTranslation();

  const { selectedProject, allTags } = useProjectsMetadata(id ?? duplicateSourceId);
  const isEditing = Boolean(id ?? duplicateSourceId);
  const isProjectEdit = Boolean(id);

  const [projectName, setProjectName] = useState<string>('');
  const { hasCreateCDProjectPermission, hasCreateQCProjectPermission } = useAppPermissions();
  const existingCameraCalibration = useCameraCalibration(id ?? duplicateSourceId);
  const [cameraCalibration, setCameraCalibration] = useState<CameraCalibration>(existingCameraCalibration);

  const { tags, setTags, selectedTag, allTagsAsObject, setSelectedTag, addSelectedTag, removeTag } =
    useProjectTagManagement(selectedProject?.tags ?? [], allTags);

  const { labelName, labelsInfo, addLabel, updateLabel, removeLabel, setLabelsInfo, availableColorOptions } =
    useProjectLabelManagement();

  const { projectStrategy, setProjectStrategy } = useProjectStrategy(selectedProject);

  useEffect(() => {
    setCameraCalibration(existingCameraCalibration);
  }, [existingCameraCalibration, setCameraCalibration]);

  // A project list refresh rebuilds selectedProject even when nothing changed. Refilling the form
  // on every one of those silently drops the edits made since - the save then stores what the
  // dialog reloaded. Fill once per edited project instead.
  const filledForProjectRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const sourceId = selectedProject?.projectId;
    if (filledForProjectRef.current === sourceId) return;
    filledForProjectRef.current = sourceId;

    setProjectName(selectedProject?.projectName ?? '');
    setTags(selectedProject?.tags ?? []);
    setLabelsInfo(
      selectedProject?.projectUseCase?.modelClasses
        ? [...selectedProject.projectUseCase.modelClasses]
            .sort((a, b) => (a.classIndex ?? 0) - (b.classIndex ?? 0))
            .map(label => ({
              labelName: label.className ?? '',
              labelColor: getColorOption(label.labelColor),
              labelCategory: '',
            }))
        : []
    );
  }, [selectedProject, setTags, setLabelsInfo]);

  useEffect(() => {
    if (labelsInfo.length === 0) {
      setLabelsInfo([backgroundLabel, { labelName: labelName, labelColor: colorOptions[0], labelCategory: '' }]);
    } else setLabelsInfo(labelsInfo);
  }, [labelsInfo, setLabelsInfo, labelName]);

  useEffect(() => {
    onChange(
      {
        labelsInfo,
        projectName,
        tags,
        strategy: projectStrategy.id,
        useCase,
      },
      cameraCalibration
    );
  }, [labelsInfo, onChange, projectName, tags, projectStrategy, useCase, cameraCalibration]);

  const noLicence =
    (useCase === UseCase.QUALITY_CHECK && !hasCreateQCProjectPermission) ||
    (useCase === UseCase.COMPONENT_DETECTION && !hasCreateCDProjectPermission);
  const flag = <DisconnectedFlag width={'1.75rem'} />;

  return (
    <>
      {noLicence ? (
        <Content>
          {flag}
          <Headline
            label={
              useCase === UseCase.QUALITY_CHECK ? t('license.qc.noLicense.title') : t('license.cd.noLicense.title')
            }
          />
          <span>
            {useCase === UseCase.QUALITY_CHECK ? t('license.qc.noLicense.details') : t('license.cd.noLicense.details')}
          </span>
        </Content>
      ) : (
        <Container data-testid='project-settings-dialog-content'>
          <ProjectNameInput
            projectName={projectName}
            setProjectName={setProjectName}
            isEditing={isEditing}
            useCase={useCase}
          />
          <ModelSelection className='match-header'>
            {useCase === UseCase.COMPONENT_DETECTION && (
              <StrategySelector
                lockSelection={Boolean(duplicateSourceId)}
                projectStrategy={projectStrategy}
                setProjectStrategy={setProjectStrategy}
                usecase={useCase}
              />
            )}
            <UseCaseAdditions
              useCase={useCase}
              setCameraCalibration={setCameraCalibration}
              cameraCalibration={cameraCalibration}
            />
          </ModelSelection>
          <LabelInput
            isEditing={isProjectEdit}
            isDuplicating={Boolean(duplicateSourceId)}
            labelsInfo={labelsInfo}
            addLabel={addLabel}
            removeLabel={removeLabel}
            updateLabel={updateLabel}
            useCase={useCase}
            containerRef={containerRef}
            availableColorOptions={availableColorOptions}
          />
          <TagInput
            tags={tags}
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            addSelectedTag={addSelectedTag}
            removeTag={removeTag}
            allTagsAsObject={allTagsAsObject}
            containerRef={containerRef}
            useCase={useCase}
            t={t}
          />
        </Container>
      )}
    </>
  );
}

// Styled Components
const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  max-width: 850px;
  min-width: 600px;

  .left-divider {
    text-align: left;
  }

  .half-pager {
    width: 50%;
  }

  .match-header {
    width: calc(100% - 1rem);
    margin-left: 0.5rem;
  }

  .push-right {
    margin-left: 0.5rem;
  }
`;

const Content = styled.div`
  display: grid;
  place-items: center;
  margin: auto;
`;

const ModelSelection = styled.div`
  display: grid;
  grid-template-columns: 2fr;
  gap: 0.5rem;
`;
